/**
 * React bindings re-export.
 *
 * Apps import from `@linkhr/i18n` (not `react-i18next` directly) so that
 * the i18n package stays the single point of integration for hooks, the
 * Trans component, and type-only exports. This makes it trivial to swap or
 * extend react-i18next later without touching app code.
 */

import { useTranslation } from "react-i18next";
import { intlLocale, intlDateLocale } from "./intlLocale";

export { I18nextProvider, Trans, useTranslation, withTranslation } from "react-i18next";
export type { TFunction } from "i18next";

/**
 * The canonical Intl locale tag for the active UI language (`"ar-JO"` / `"en-GB"`).
 * Ergonomic accessor over `intlLocale(i18n.language)` for component call sites
 * that format dates/numbers/money — see `intlLocale` for the mapping rationale.
 */
export function useIntlLocale(): string {
  const { i18n } = useTranslation();
  return intlLocale(i18n.language);
}

/**
 * The DATE-ONLY Intl locale tag for the active UI language (`"ar-EG"` /
 * `"en-GB"`). Ergonomic accessor over `intlDateLocale(i18n.language)` for
 * component call sites that format Gregorian month / weekday names — use this
 * (not `useIntlLocale`) so Arabic months render يوليو-style. See
 * `intlDateLocale` for the policy rationale. Numbers/currency stay on
 * `useIntlLocale`.
 */
export function useIntlDateLocale(): string {
  const { i18n } = useTranslation();
  return intlDateLocale(i18n.language);
}
